<template>
  <NuxtLink
    class="group relative block w-full overflow-hidden bg-black1"
    :class="variant === 'large' ? 'aspect-[16/9]' : 'aspect-[4/5]'"
    :data-testid="`destination-card-${destination.id}`"
    :to="detailPageLink"
    @click="handleClick"
  >
    <OptimizedImage
      v-if="imageUrl"
      class="absolute inset-0 h-full w-full object-cover transition-transform duration-500 md:group-hover:scale-[1.04] md:group-hover:will-change-transform"
      :alt="destination.titleEn"
      :fetchpriority="loading === 'eager' ? 'high' : undefined"
      :image-url="imageUrl"
      :loading="loading"
      :optimized-image-options="{ width: variant === 'large' ? 1216 : 640 }"
    />
    <!-- 画像視認性のためのフィルター (ヘッダー Destinations ドロップダウンと統一): 下方向グラデーション + 全面ダーク -->
    <div aria-hidden="true" class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
    <div aria-hidden="true" class="absolute inset-0 bg-black/20" />

    <!-- タイトル: EN名 + 縦JP名(上下に細い白線) -->
    <div class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-y-3 text-white">
      <p
        class="text-center font-Spectral font-medium capitalize tracking-[0.5px] drop-shadow-[0_4px_12px_rgba(0,0,0,0.4)]"
        :class="variant === 'large' ? 'text-[24px] leading-9 md:text-[30px]' : 'text-[20px] leading-7 md:text-[24px]'"
      >
        {{ destination.titleEn }}
      </p>
      <div v-if="destination.titleJa" class="flex flex-col items-center gap-y-2">
        <span aria-hidden="true" class="h-8 w-px bg-white/80" />
        <p class="vertical-text text-center font-NotoSerifJP font-medium tracking-wider drop-shadow-[0_4px_12px_rgba(0,0,0,0.4)]" :class="variant === 'large' ? 'text-[18px]' : 'text-[16px]'">
          {{ destination.titleJa }}
        </p>
        <span aria-hidden="true" class="h-8 w-px bg-white/80" />
      </div>
    </div>

    <!-- view more: 表示はデスクトップ小カードのみ (Figma 指定) -->
    <p v-if="showViewMore" class="pointer-events-none absolute bottom-3 right-3 font-Spectral text-sm text-beige1 drop-shadow-[0_4px_12px_rgba(0,0,0,0.4)]">view more</p>
  </NuxtLink>
</template>

<script lang="ts" setup>
import OptimizedImage from '@/wabunka-src/common/components/stateful/OptimizedImage/OptimizedImage.vue';
import { generateDetailPageLink, type MinimumFeaturedViewModel } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';

interface Props {
  destination: MinimumFeaturedViewModel;
  variant?: 'small' | 'large';
  /** 「view more」テキスト表示有無。Figma上ではデスクトップ小カードのみ表示。 */
  showViewMore?: boolean;
  /** 画像読み込み戦略。LCP候補となる先頭カードは 'eager' を指定する。 */
  loading?: 'lazy' | 'eager';
  onClick?: (destination: MinimumFeaturedViewModel) => void;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'small',
  showViewMore: false,
  loading: 'lazy',
  onClick: undefined,
});

// 飛び先は type に応じて生成する。Top variant では type=destination のため /{id}/。
const detailPageLink = computed(() => generateDetailPageLink(props.destination));

// variant に応じて画像比率に近いメディアを優先（共通 getBackgroundImageUrl は tall 優先のため使えない）
const imageUrl = computed(() => {
  const { backgroundWideMedias, backgroundTallMedias } = props.destination;
  const wide = backgroundWideMedias?.[0]?.url ?? '';
  const tall = backgroundTallMedias?.[0]?.url ?? '';
  if (props.variant === 'large') {
    return wide || tall;
  }
  return tall || wide;
});

const handleClick = () => {
  props.onClick?.(props.destination);
};
</script>

<style lang="scss" scoped>
.vertical-text {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  -webkit-writing-mode: vertical-rl;
  -ms-writing-mode: tb-rl;
  -webkit-text-orientation: mixed;
}
</style>
